import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Groups,
  TrendingUp,
  Security,
  Public,
  ExpandMore,
  HelpOutline,
  BusinessCenter,
  AutoAwesome,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import aboutbanner from "../Assests/Images/about-hero.png";
import aboutdis from "../Assests/Images/aboutdis.png";

function About() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedFaq, setExpandedFaq] = useState(null); // Track which FAQ is expanded

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);

  const faqs = [
    {
      q: "How can I reset my password?",
      a: 'If you have forgotten your password, click on the "Forgot Password" option on the login page. Enter your registered email address, and you will receive instructions to securely reset your password.',
    },
    {
      q: "How long does the hiring and interview process typically take?",
      a: "The duration varies depending on the role and assessment requirements. In general, coding assessments take approximately 45–60 minutes, while technical and HR interviews typically range from 30–45 minutes each.",
    },
    {
      q: "Can I apply for multiple job opportunities?",
      a: "Yes. Candidates may apply for multiple positions, provided they meet the eligibility criteria and qualifications specified for each role.",
    },
    {
      q: "Is ExiHire free for job seekers?",
      a: "Yes. ExiHire is completely free for candidates, enabling them to explore opportunities, participate in assessments, and attend interviews without any cost.",
    },
    {
      q: "Can organizations customize their interview process?",
      a: "Absolutely. Organizations can tailor coding assessments, technical evaluations, HR interview rounds, and hiring workflows to align with their specific recruitment requirements.",
    },
    {
      q: "How does ExiHire ensure the security of my data?",
      a: "We prioritize data security through industry-standard encryption, secure infrastructure, and best-practice privacy measures to safeguard user information at every stage.",
    },
    {
      q: "Does ExiHire support remote hiring and virtual interviews?",
      a: "Yes. ExiHire is designed to support end-to-end remote hiring with integrated tools for online assessments, virtual interviews, and collaborative evaluation processes.",
    },
    {
      q: "Which industries and sectors does ExiHire support?",
      a: "ExiHire supports organizations across a wide range of industries, including technology, finance, healthcare, education, consulting, manufacturing, and many others.",
    },
  ];

  const values = [
    {
      icon: <Groups fontSize="large" />,
      title: "Our Mission",
      description:
        "To simplify, automate, and streamline the hiring process for companies while providing a seamless and fair experience for candidates.",
    },
    {
      icon: <TrendingUp fontSize="large" />,
      title: "Our Vision",
      description:
        "To become the global standard for automated technical and HR assessments, delivering value and efficiency for both organizations and candidates.",
    },
    {
      icon: <Security fontSize="large" />,
      title: "Our Values",
      description:
        "Integrity, transparency, and fairness are at the heart of everything we build.",
    },
    {
      icon: <Public fontSize="large" />,
      title: "Global Reach",
      description:
        "Connecting top talent with the best opportunities across the world.",
    },
  ];

  const filteredFaqs = faqs.filter((faq) =>
    faq.q.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleAccordionChange = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: "#F8FAFC", backgroundImage: "radial-gradient(#E2E8F0 1px, transparent 1px)", backgroundSize: "20px 20px" }}>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: "#0A0F2C",
          color: "white",
          pt: { xs: 3, md: 4 },
          pb: { xs: 4, md: 5 },
          overflow: "hidden",
          position: "relative"
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: { xs: '100%', lg: '1200px', xxl: '1440px', xxxl: '1800px' } }}>
          <Grid container spacing={4} alignItems="center">
            {/* Left side content */}
            <Grid item xs={12} md={7} sx={{ pr: { md: 10 } }}>
              <Box>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', sm: '3.5rem', md: '5rem' },
                    fontWeight: 900,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.1,
                    mb: 2,
                    textTransform: 'uppercase',
                    background: 'linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0.7) 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  ABOUT US
                </Typography>
                <Box
                  sx={{
                    width: 80,
                    height: 4,
                    background: 'linear-gradient(90deg, #1E40AF 0%, #3B82F6 100%)',
                    borderRadius: '2px',
                    mb: 4
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    color: 'rgba(255, 255, 255, 0.8)',
                    mb: 1,
                    fontWeight: 400
                  }}
                >
                  Learn more about ExiHire and how we're revolutionizing the hiring process.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    color: 'rgba(255, 255, 255, 0.8)',
                    mb: 4,
                    fontWeight: 400
                  }}
                >
                  Discover our mission, values, and commitment to connecting top talent with companies worldwide.
                </Typography>

                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {[
                    { icon: <Groups />, text: "Our Team" },
                    { icon: <Security />, text: "Data Security" },
                    { icon: <AutoAwesome />, text: "Smart Matching" },
                    { icon: <BusinessCenter />, text: "End-to-End Hiring" },
                    { icon: <Public />, text: "Global Reach" }
                  ].map((item, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          bgcolor: 'rgba(67, 56, 202, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#1E3A8A',
                          border: '1px solid rgba(67, 56, 202, 0.3)'
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '1.1rem', color: 'white' }}>
                        {item.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box
                component="img"
                src={aboutbanner}
                alt="About Hero"
                sx={{
                  width: { xs: '100%', md: '130%' },
                  maxWidth: { xs: '100%', md: '800px' },
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: '16px',
                  display: 'block'
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxWidth={false} sx={{ maxWidth: { xs: '100%', lg: '1200px', xxl: '1440px', xxxl: '1800px' }, py: { xs: 8, md: 10 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={7}>
            {/* Badge */}
            <Box
              sx={{
                display: "inline-block",
                px: 2,
                py: 0.5,
                borderRadius: "20px",
                background: "linear-gradient(90deg, #1E3A8A 0%, #1E3A8A 100%)",
                mb: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                OUR PURPOSE
              </Typography>
            </Box>

            {/* Heading */}
            <Typography
              variant="h2"
              sx={{
                fontWeight: "bold",
                color: "#1E3A8A",
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                mb: 1,
              }}
            >
              Our Purpose
            </Typography>

            {/* Gradient Underline */}
            <Box
              sx={{
                width: "80px",
                height: "4px",
                background: "linear-gradient(90deg, #1E3A8A 0%, #9333EA 100%)",
                borderRadius: "2px",
                mb: 4,
              }}
            />

            {/* Purpose Items */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    minWidth: 60,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 10px 15px -3px rgba(30, 58, 138, 0.4)",
                  }}
                >
                  <Groups sx={{ color: "white", fontSize: 30 }} />
                </Box>
                <Typography variant="body1" sx={{ color: "#374151", fontSize: "1.1rem", lineHeight: 1.6 }}>
                  ExiHire is a cutting-edge platform designed to revolutionize how
                  organizations conduct interviews. We provide a comprehensive suite
                  of tools that cover the entire recruitment lifecycle, from job
                  posting to offer letter generation.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    minWidth: 60,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 10px 15px -3px rgba(30, 58, 138, 0.4)",
                  }}
                >
                  <BusinessCenter sx={{ color: "white", fontSize: 30 }} />
                </Box>
                <Typography variant="body1" sx={{ color: "#374151", fontSize: "1.1rem", lineHeight: 1.6 }}>
                  Our platform integrates automated coding tests, structured
                  technical interviews, and HR assessment modules, ensuring a
                  holistic evaluation of every candidate.
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box
              component="img"
              src={aboutdis}
              alt="ExiHire AI Hiring Platform"
              sx={{
                width: '100%',
                maxWidth: '600px',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '16px',
                display: 'block',
                boxShadow: 3
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Values Section */}
      <Box sx={{ bgcolor: "background.default", py: { xs: 1, md: 2 } }}>
        <Container maxWidth={false} sx={{ maxWidth: { xs: '100%', lg: '1200px', xxl: '1440px', xxxl: '1800px' } }}>
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: "bold",
              color: "#1E40AF",
              mb: { xs: 4, md: 6 },
              fontSize: { xs: "2rem", md: "3rem" },
            }}
            gutterBottom
          >
            Our Core Pillars
          </Typography>
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    textAlign: "center",
                    p: 2,
                    borderRadius: 3,
                    boxShadow: 3,
                  }}
                >
                  <CardContent>
                    <Box sx={{ color: "primary.main", mb: 2 }}>
                      {value.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {value.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {value.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Animated FAQ Section */}
      <Box id="faq" sx={{ mt: { xs: 2, md: 4 }, textAlign: "center", px: 2, pb: 8, scrollMarginTop: '100px' }}>
        <Typography
          variant="h4"
          gutterBottom
          align="center"
          sx={{
            fontWeight: "bold",
            color: "#1E40AF",
            fontSize: { xs: "1.75rem", md: "2.125rem" },
          }}
        >
          Frequently Asked Questions
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Have questions? We're here to help you choose the right plan.
        </Typography>

        {/* Search Bar */}
        <TextField
          label="Search FAQs..."
          variant="outlined"
          fullWidth
          sx={{ mb: 4, maxWidth: 600, mx: "auto" }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <Box sx={{ mt: 6, maxWidth: 800, mx: "auto", textAlign: "left" }}>
          {filteredFaqs.length ? (
            filteredFaqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
                style={{ marginBottom: "16px" }}
              >
                <Accordion
                  expanded={expandedFaq === index}
                  onChange={() => handleAccordionChange(index)}
                  sx={{
                    borderRadius: 3,
                    boxShadow: 3,
                    "&:before": { display: "none" },
                    transition: "all 0.3s",
                    "&:hover": { boxShadow: 6 },
                    "&.Mui-expanded": {
                      margin: 0,
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      "& .MuiAccordionSummary-content": {
                        margin: "12px 0",
                      },
                      "& .MuiAccordionSummary-content.Mui-expanded": {
                        margin: 0,
                      },
                      "&.Mui-expanded": {
                        minHeight: "48px",
                      },
                    }}
                  >
                    <HelpOutline sx={{ mr: 2, color: "primary.main" }} />
                    <Typography fontStyle="italic" fontWeight={500} sx={{ textAlign: "left" }}>
                      {faq.q}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 1, pl: 7, textAlign: "left" }}>
                    <Typography color="text.secondary" sx={{ m: 0, textAlign: "left" }}>
                      {faq.a}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </motion.div>
            ))
          ) : (
            <Typography variant="body1" color="text.secondary">
              No FAQs match your search.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default About;
